import { Trans } from "../Translate";
import React from "react";
import { AvatarWithName } from "../Avatar";
import FormattedTime from "../FormattedTime";
import { IconSquareCheckFilled, IconSquareChevronsLeftFilled, IconFlag, IconFileText } from "../icons";
import { ActivityProps, AcknowledgmentProps } from "./types";

export function MilestoneCompletedActivity({ activity, formattedTimePreferences }: ActivityProps) {
  return (
    <div className="flex items-center justify-between gap-3 py-3 px-4 not-first:border-t border-stroke-base text-content-accent relative">
      <div className="flex-1">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Trans
              i18nKey="<author/> <icon/><text>completed the milestone</text>"
              components={{
                author: (
                  <AvatarWithName
                    person={activity.author}
                    size="normal"
                    nameFormat="short"
                    link={activity.author.profileLink}
                    className="font-semibold"
                  />
                ),
                icon: <IconSquareCheckFilled size={20} className="text-accent-1" />,
                text: <div className="pr-2 font-semibold text-content-accent" />,
              }}
            />
          </div>

          <div className="flex items-center justify-between gap-2">
            <span className="text-content-dimmed text-sm">
              <FormattedTime {...formattedTimePreferences} time={activity.insertedAt} format="relative" />
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export function MilestoneReopenedActivity({ activity, formattedTimePreferences }: ActivityProps) {
  return (
    <div className="flex items-center justify-between gap-3 py-3 px-4 not-first:border-t border-stroke-base text-content-accent relative">
      <div className="flex-1">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Trans
              i18nKey="<author/> <icon/><text>re-opened the milestone</text>"
              components={{
                author: (
                  <AvatarWithName
                    person={activity.author}
                    size="normal"
                    nameFormat="short"
                    link={activity.author.profileLink}
                    className="font-semibold"
                  />
                ),
                icon: <IconSquareChevronsLeftFilled size={20} className="text-yellow-500" />,
                text: <div className="pr-2 font-semibold text-content-accent" />,
              }}
            />
          </div>

          <div className="flex items-center justify-between gap-2">
            <span className="text-content-dimmed text-sm">
              <FormattedTime {...formattedTimePreferences} time={activity.insertedAt} format="relative" />
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export function MilestoneCreatedActivity({ activity, formattedTimePreferences }: ActivityProps) {
  return (
    <div className="flex gap-3 py-1.5 px-4 text-content-subtle text-sm relative">
      <div className="shrink-0 mt-0.5">
        <IconFlag size={16} className="text-blue-500" />
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-1.5">
          {activity.content ? (
            <>
              <MilestoneAuthor activity={activity} />
              <span className="min-w-0">{activity.content}</span>
            </>
          ) : (
            <Trans
              i18nKey="<author/> <text>created the milestone</text>"
              components={{ author: <MilestoneAuthor activity={activity} />, text: <span className="min-w-0" /> }}
            />
          )}
        </div>
      </div>

      <div className="shrink-0 mt-0.5">
        <span className="text-content-subtle text-xs">
          <FormattedTime {...formattedTimePreferences} time={activity.insertedAt} format="relative" />
        </span>
      </div>
    </div>
  );
}

export function MilestoneDescriptionActivity({ activity, formattedTimePreferences }: ActivityProps) {
  return (
    <div className="flex gap-3 py-1.5 px-4 text-content-subtle text-sm relative">
      <div className="shrink-0 mt-0.5">
        <IconFileText size={16} className="text-content-dimmed" />
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-1.5">
          {activity.content ? (
            <>
              <MilestoneAuthor activity={activity} />
              <span className="min-w-0">{activity.content}</span>
            </>
          ) : (
            <Trans
              i18nKey="<author/> <text>added a description</text>"
              components={{ author: <MilestoneAuthor activity={activity} />, text: <span className="min-w-0" /> }}
            />
          )}
        </div>
      </div>

      <div className="shrink-0 mt-0.5">
        <span className="text-content-subtle text-xs">
          <FormattedTime {...formattedTimePreferences} time={activity.insertedAt} format="relative" />
        </span>
      </div>
    </div>
  );
}

export function MilestoneUpdateActivity({ activity, formattedTimePreferences }: ActivityProps) {
  return (
    <div className="flex gap-3 py-1.5 px-4 text-content-subtle text-sm relative">
      <div className="shrink-0 mt-0.5">
        <IconFlag size={16} className="text-content-dimmed" />
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-1.5">
          {activity.content ? (
            <>
              <MilestoneAuthor activity={activity} />
              <span className="min-w-0">{activity.content}</span>
            </>
          ) : (
            <Trans
              i18nKey="<author/> <text>updated the milestone</text>"
              components={{ author: <MilestoneAuthor activity={activity} />, text: <span className="min-w-0" /> }}
            />
          )}
        </div>
      </div>

      <div className="shrink-0 mt-0.5">
        <span className="text-content-subtle text-xs">
          <FormattedTime {...formattedTimePreferences} time={activity.insertedAt} format="relative" />
        </span>
      </div>
    </div>
  );
}

export function AcknowledgmentActivity({ person, ackAt, formattedTimePreferences }: AcknowledgmentProps) {
  return (
    <div className="flex items-center justify-between gap-3 py-6 px-4 not-first:border-t border-stroke-base text-content-accent">
      <div className="flex items-center justify-between flex-1">
        <div className="flex items-center gap-2 font-bold flex-1">
          <Trans
            i18nKey="<author/> <text>acknowledged this Check-In</text><icon/>"
            components={{
              author: (
                <AvatarWithName
                  person={person}
                  size="normal"
                  nameFormat="short"
                  link={person.profileLink}
                  className="font-semibold"
                />
              ),
              text: <span />,
              icon: <IconSquareCheckFilled size={24} className="text-accent-1" />,
            }}
          />
        </div>

        <div className="flex items-center justify-between">
          <span className="text-content-dimmed text-sm">
            <FormattedTime {...formattedTimePreferences} time={ackAt} format="relative" />
          </span>
        </div>
      </div>
    </div>
  );
}

function MilestoneAuthor({ activity }: Pick<ActivityProps, "activity">) {
  return (
    <AvatarWithName
      person={activity.author}
      size="tiny"
      textSize="small"
      nameFormat="short"
      link={activity.author.profileLink}
      className="text-content-dimmed font-medium"
      showAvatar={false}
    />
  );
}
